// "Complete files" of one batch (TSD §5): HR drops the KTP photos and HR
// documents the rows need; each file finds its row by the NIK (or email /
// Employee ID) in its name and its kind by type and keyword; the rest waits
// for HR's choice. "Save N files" uploads them one row file per request, three
// at a time. The backend answers every upload with the row's files, status
// and missing list, which replace the row in place; the batch and the rows
// table are read again once the uploads are done.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { debounce } from 'lodash'
import { SearchLg, Trash01, UploadCloud02 } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyColumn,
  MyConfirmModal,
  MyDataTable,
  MyHorizontalTabV2,
  MyTextField,
  myToaster,
} from '@interstellar-component'
import paginatorToMeta from '@src/services/pagination'
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import ENQUIRY_CATEGORY_LABEL from '@src/services/enquiryCategory'
import Service from '../service'
import usePagedList from '../usePagedList'
import useStagingQueue from '../useStagingQueue'
import { isActive } from '../uploadQueue'
import { unstageError } from '../errors'
import {
  FILE_KINDS,
  batchFileKinds,
  cellState,
  checkFileFor,
  duplicateContent,
  fileNikMismatch,
  hashFile,
  hashFiles,
  keyMode as keyModeOf,
  matchFiles,
  mergePending,
  nextEntryId,
  pendingKey,
  reconcilePending,
  rowNeeds,
  sameFile,
} from '../fileMatcher'
import {
  MAX_DROP_FILES,
  ROWS_HIDDEN_TEXT,
  ROW_STATUS,
  SEARCH_MAX_LENGTH,
  STAGE_LIMITS,
  breakdownText,
  chipOf,
  nikMismatchText,
  plural,
  pluralVerb,
  rejectReasonText,
  requiredFileLabels,
} from '../constants'
import StatusChip from './StatusChip'
import Notice from './Notice'
import LoadError from './LoadError'
import FileDropArea from './FileDropArea'
import FileCell from './FileCell'
import FilePreview from './FilePreview'
import AssignFileModal, { rowMatchesSearch } from './AssignFileModal'
import UnmatchedTray, { RejectedFiles } from './UnmatchedTray'

const PAGE_SIZE = 50
const FILTERS = [
  { value: 'all', label: 'All rows' },
  { value: 'needs', label: 'Needs a file' },
  { value: 'pending', label: 'Not saved yet' },
]
const acceptOf = (types) => Object.keys(types).map((ext) => `.${ext}`).join(',')
const PHOTO_ACCEPT = acceptOf(STAGE_LIMITS.photo.types)
const DOCUMENT_ACCEPT = acceptOf(STAGE_LIMITS.document.types)

// Upload order: by row, then photo / T&C / letter.
const uploadOrder = (a, b) => a.rowNo - b.rowNo || FILE_KINDS.indexOf(a.kind) - FILE_KINDS.indexOf(b.kind)

// Incoming files that are not already listed (the same folder dropped twice).
const notListed = (list, incoming) =>
  incoming.filter((entry) => !list.some((listed) => sameFile(listed.file, entry.file)))

function NamingHelp({ mode, kinds }) {
  const example = (kind) => {
    const key = mode === 'nik' ? '<NIK>' : '<email or Employee ID>'
    if (kind === 'ktp_photo') return `${key}.jpg`
    if (kind === 'terms_condition') return `${key}_tnc.pdf`
    return `${key}_lamaran.pdf`
  }
  return (
    <span className="flex flex-col gap-0.5">
      <span>
        {mode === 'nik'
          ? 'Name each file with the NIK of its row: '
          : 'Name each file with the email (as in the Email column) or the Employee ID of its row: '}
        {kinds.map((kind, i) => (
          <React.Fragment key={kind}>
            {i > 0 && ', '}
            <code className="rounded bg-gray-100 px-1 font-mono text-[11px] text-gray-700">{example(kind)}</code>{' '}
            ({FILE_KIND_LABEL[kind]}
            {kind === 'job_application_form' ? ', candidates' : ''})
          </React.Fragment>
        ))}
        .
      </span>
      <span>
        Documents are told apart by a word in the name: tnc, t&amp;c, syarat or terms for the T&amp;C; lamaran or
        application for the job application form. Files that do not match wait for your choice.
      </span>
    </span>
  )
}
NamingHelp.propTypes = {
  mode: PropTypes.oneOf(['nik', 'email']).isRequired,
  kinds: PropTypes.arrayOf(PropTypes.string).isRequired,
}

function PersonCell({ row }) {
  return (
    <div className="flex max-w-[240px] min-w-0 flex-col gap-0.5 py-1">
      <span className="truncate text-sm font-medium text-gray-900" title={row.full_name || ''}>
        {row.full_name || '—'}
      </span>
      <span className="truncate text-xs text-gray-500" title={row.email || ''}>
        {row.email || '—'}
      </span>
      <span className="text-xs text-gray-500">
        {ENQUIRY_CATEGORY_LABEL[row.category] || row.category}
        {row.employee_code ? ` · ID ${row.employee_code}` : ''}
      </span>
    </div>
  )
}
PersonCell.propTypes = { row: PropTypes.object.isRequired }

/**
 * The file grid of an open (or processing) batch. `refreshKey` changes when
 * the page changed the rows itself (settings re-applied, a row removed);
 * `onChanged` asks the page to read the batch and its rows again (counts,
 * can_process); `onDirtyChange(bool)`: leaving now would lose something (files
 * not saved yet, uploads running, files waiting for HR's choice);
 * `onPendingChange({ unsaved, uploading })`: files added here and not saved
 * yet, and whether files are being checked or uploaded (Process waits for
 * both: it takes each row's saved files).
 */
function FileCompletionGrid({ batch, refreshKey, onChanged, onDirtyChange, onPendingChange }) {
  const batchKinds = useMemo(() => batchFileKinds(batch.required_files), [batch.required_files])
  const mode = keyModeOf(batch)

  // ── Rows waiting for files ────────────────────────────────────────────
  const fetchTargets = useCallback(() => Service.pendingRows(batch.id), [batch.id])
  const read = usePagedList(fetchTargets)
  const { reload: reloadTargets } = read
  const lastRefresh = useRef(refreshKey)
  useEffect(() => {
    if (lastRefresh.current === refreshKey) return
    lastRefresh.current = refreshKey
    reloadTargets()
  }, [refreshKey, reloadTargets])

  // A stage / unstage answer carries the row's new files, status and missing
  // list: laid over the last read until the next one.
  const dataRef = useRef(read.data)
  dataRef.current = read.data
  const [patches, setPatches] = useState({ base: null, rows: new Map() })
  const patchRow = useCallback((rowId, data) => {
    if (!data?.files) return
    setPatches((prev) => {
      const base = dataRef.current
      const rows = new Map(prev.base === base ? prev.rows : [])
      rows.set(rowId, { status: data.status, missing: data.missing, files: data.files })
      return { base, rows }
    })
  }, [])
  const targets = useMemo(() => {
    const rows = patches.base === read.data ? patches.rows : null
    if (!rows?.size) return read.data
    return read.data.map((row) => (rows.has(row.id) ? { ...row, ...rows.get(row.id) } : row))
  }, [read.data, patches])
  const targetsRef = useRef(targets)
  targetsRef.current = targets
  const rowsById = useMemo(() => new Map(targets.map((row) => [row.id, row])), [targets])
  const loaded = !read.loading && !read.error

  // A kind the batch no longer takes still gets a column while a row holds a
  // leftover file of it (HR may remove it).
  const gridKinds = useMemo(
    () => FILE_KINDS.filter((kind) => batchKinds.includes(kind) || targets.some((row) => row.files?.[kind]?.leftover)),
    [batchKinds, targets]
  )

  // ── Files added in this browser ───────────────────────────────────────
  const [pending, setPending] = useState(() => new Map()) // pendingKey -> entry
  const pendingRef = useRef(pending)
  pendingRef.current = pending
  const [tray, setTray] = useState([])
  const [rejected, setRejected] = useState([])
  const [skipped, setSkipped] = useState([]) // the same as the saved file: left out
  // The lists as last rendered, for an intake that resumes after hashing.
  const listsRef = useRef({ tray, rejected, skipped })
  listsRef.current = { tray, rejected, skipped }
  const [checking, setChecking] = useState(null) // { done, total } while a drop is hashed
  const [intake, setIntake] = useState(null) // what the last drop / pick did
  const [setAside, setSetAside] = useState(0) // waiting files whose row stopped taking them
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Add entries to the waiting files, against the rows as they are now.
  const addPending = useCallback((entries, { without = null } = {}) => {
    setPending((prev) => {
      let base = prev
      if (without) {
        base = new Map(prev)
        if (base.get(without.key)?.id === without.id) base.delete(without.key)
      }
      return reconcilePending(mergePending(base, entries).pending, targetsRef.current).pending
    })
  }, [])

  // ── Uploads ───────────────────────────────────────────────────────────
  const [runIds, setRunIds] = useState(() => new Set())
  const [runSummary, setRunSummary] = useState(null)
  const [fatal, setFatal] = useState(null)
  const queue = useStagingQueue({
    batchId: batch.id,
    onSaved: (item, data) => {
      setPending((prev) => {
        const key = pendingKey(item.rowId, item.kind)
        if (prev.get(key)?.id !== item.id) return prev
        const next = new Map(prev)
        next.delete(key)
        return next
      })
      patchRow(item.rowId, data)
    },
    onFatal: (failure) => setFatal(failure.message),
    onDrain: (summary) => {
      setRunSummary(summary)
      if (summary.saved + summary.failed > 0) {
        // Every saved file's answer already replaced its row (read from the
        // primary); a read of the rows right now may come from a lagging
        // replica and show those files as missing again. They are read again
        // only when an upload failed (its row may have changed under it).
        if (summary.failed > 0) reloadTargets()
        onChanged?.()
      }
    },
  })
  const queueById = useMemo(() => new Map(queue.items.map((item) => [item.id, item])), [queue.items])
  const { forget } = queue

  // After every read of the rows: drop what no row takes any more (and the
  // failed uploads of it: their row is gone from the grid).
  useEffect(() => {
    if (!loaded) return
    const { pending: next, dropped } = reconcilePending(pendingRef.current, targets)
    const current = pendingRef.current
    const unchanged =
      !dropped.length &&
      [...next].every(([key, entry]) => {
        const before = current.get(key)
        return before && before.rowNo === entry.rowNo && before.replaces === entry.replaces
      })
    if (unchanged) return
    setPending(next)
    if (dropped.length) {
      dropped.forEach((entry) => forget(entry.id))
      setSetAside((n) => n + dropped.length)
    }
  }, [targets, loaded, forget])

  const pendingList = useMemo(() => [...pending.values()], [pending])
  const duplicates = useMemo(() => duplicateContent(pendingList), [pendingList])
  const saveable = pendingList.filter((entry) => {
    const item = queueById.get(entry.id)
    if (item && isActive(item)) return false
    if (item?.state === 'failed' && !item.error?.retryable) return false
    return !duplicates.get(entry.id)?.blocking
  })
  const blocked = pendingList.filter((entry) => duplicates.get(entry.id)?.blocking)
  const runItems = queue.items.filter((item) => runIds.has(item.id))
  const runDone = runItems.filter((item) => !isActive(item)).length
  const runSaved = runItems.filter((item) => item.state === 'saved').length
  const runFailed = runItems.filter((item) => item.state === 'failed').length
  const retryable = queue.items.some(
    (item) => item.state === 'not_sent' || (item.state === 'failed' && item.error?.retryable)
  )

  // ── Leaving with unsaved files ────────────────────────────────────────
  const uploading = queue.running || Boolean(checking)
  const dirty = pending.size > 0 || tray.length > 0 || uploading
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])
  const unsaved = pending.size
  useEffect(() => {
    onPendingChange?.({ unsaved, uploading })
  }, [unsaved, uploading, onPendingChange])
  useEffect(() => () => onPendingChange?.({ unsaved: 0, uploading: false }), [onPendingChange])

  // SHA-256 of files about to go to rows (the "same content on two rows"
  // checks), two at a time, with "Checking N of M files…" meanwhile.
  const hashEntries = (entries) => {
    setChecking({ done: 0, total: entries.length })
    return hashFiles(
      entries.map((entry) => entry.file),
      {
        concurrency: 2,
        // Every 10th file: a big folder must not re-render the grid per file.
        onProgress: (done, total) => {
          if (mounted.current && (done % 10 === 0 || done === total)) setChecking({ done, total })
        },
      }
    )
  }

  // ── Intake: a drop, "Choose files" or "Choose folder" ─────────────────
  const intakeFiles = async (files, { truncated = false, unreadable = 0 } = {}) => {
    const result = matchFiles(files, targetsRef.current, { keyMode: mode, batchKinds })
    const matched = result.matched.filter((entry) => !entry.alreadySaved)
    // Hashed: the files that go to a row now or later (not the unusable ones,
    // not the ones left out as already saved).
    const hashes = await hashEntries([...matched, ...result.unmatched])
    if (!mounted.current) return
    setChecking(null)
    const fresh = matched.map((entry, i) => ({ ...entry, sha256: hashes[i] }))
    const unmatched = result.unmatched.map((entry, i) => ({ ...entry, sha256: hashes[matched.length + i] }))

    const merged = mergePending(pendingRef.current, fresh)
    addPending(fresh)
    const lists = listsRef.current
    const newTray = notListed(lists.tray, unmatched)
    const newRejected = notListed(lists.rejected, result.rejected)
    const newSkipped = notListed(lists.skipped, result.matched.filter((entry) => entry.alreadySaved))
    setTray((prev) => [...prev, ...newTray])
    setRejected((prev) => [...prev, ...newRejected])
    setSkipped((prev) => [...prev, ...newSkipped])
    setIntake({
      total: result.matched.length + result.unmatched.length + result.rejected.length,
      matched: fresh.length - merged.same.length,
      replacing: fresh.filter((entry) => entry.replaces).length,
      skipped: newSkipped.length,
      tray: newTray.length,
      rejected: newRejected.length,
      replacedPending: merged.replaced.length,
      same: merged.same.length + (unmatched.length - newTray.length) + (result.rejected.length - newRejected.length),
      truncated,
      unreadable,
    })
  }
  const onFiles = (files, info) =>
    intakeFiles(files, info).catch(() => {
      if (!mounted.current) return
      setChecking(null)
      myToaster({ status: 500, message: 'The files could not be checked. Try again.' })
    })

  // ── One cell: "Choose file" / "Replace" ───────────────────────────────
  const pickInput = useRef(null)
  const pickTarget = useRef(null)
  const chooseFor = (row, kind) => {
    pickTarget.current = { rowId: row.id, kind }
    const input = pickInput.current
    if (!input) return
    input.accept = kind === 'ktp_photo' ? PHOTO_ACCEPT : DOCUMENT_ACCEPT
    input.click()
  }
  const onPicked = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const target = pickTarget.current
    pickTarget.current = null
    const row = target ? targetsRef.current.find((r) => r.id === target.rowId) : null
    if (!file || !row) return
    const check = checkFileFor(file, target.kind)
    if (check.reject) {
      myToaster({
        status: 400,
        message: rejectReasonText({ reason: check.reject, family: check.family, kind: target.kind }),
      })
      return
    }
    if (fileNikMismatch(file.name, row.nik)) {
      myToaster({ status: 409, message: nikMismatchText(row.row_no) })
      return
    }
    const need = rowNeeds(row, target.kind)
    if (need !== 'missing' && need !== 'staged') return
    const sha256 = await hashFile(file)
    if (!mounted.current) return
    addPending([
      {
        id: nextEntryId(),
        file,
        family: target.kind === 'ktp_photo' ? 'photo' : 'document',
        kind: target.kind,
        typeFix: check.typeFix,
        rowId: row.id,
        rowNo: row.row_no,
        replaces: need === 'staged',
        sha256,
      },
    ])
  }

  // ── Assign / reassign / discard ───────────────────────────────────────
  const [assigning, setAssigning] = useState(null)
  const onAssign = ({ rowId, rowNo, kind }) => {
    const entry = assigning
    setAssigning(null)
    if (!entry) return
    const row = rowsById.get(rowId)
    const moved = {
      id: nextEntryId(),
      file: entry.file,
      family: entry.family,
      kind,
      typeFix: checkFileFor(entry.file, kind).typeFix || null,
      rowId,
      rowNo,
      replaces: rowNeeds(row, kind) === 'staged',
      sha256: entry.sha256 ?? null,
    }
    if (entry.rowId) {
      queue.forget(entry.id)
      addPending([moved], { without: { key: pendingKey(entry.rowId, entry.kind), id: entry.id } })
    } else {
      setTray((prev) => prev.filter((t) => t.id !== entry.id))
      addPending([moved])
    }
  }
  const discard = (entry) => {
    queue.forget(entry.id)
    setPending((prev) => {
      const key = pendingKey(entry.rowId, entry.kind)
      if (prev.get(key)?.id !== entry.id) return prev
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }
  const discardAll = () => {
    const keep = new Map()
    pendingRef.current.forEach((entry, key) => {
      const item = queueById.get(entry.id)
      if (item && isActive(item)) keep.set(key, entry)
      else queue.forget(entry.id)
    })
    setPending(keep)
  }
  // Files first left out as already saved: hashed now (they were not at the
  // drop), so the same-photo-on-two-rows check covers them too.
  const saveSkipped = async () => {
    const list = skipped
    setSkipped([])
    const hashes = await hashEntries(list)
    if (!mounted.current) return
    setChecking(null)
    addPending(list.map((entry, i) => ({ ...entry, alreadySaved: false, sha256: hashes[i] })))
  }

  // ── Save ──────────────────────────────────────────────────────────────
  const startSave = () => {
    const entries = [...saveable].sort(uploadOrder)
    if (!entries.length) return
    setFatal(null)
    setRunSummary(null)
    setRunIds(new Set(entries.map((entry) => entry.id)))
    queue.save(entries)
  }
  const retryOne = (entry) => {
    setRunIds((prev) => new Set([...prev, entry.id]))
    setFatal(null)
    queue.retry(entry.id)
  }
  const retryAll = () => {
    const ids = queue.items
      .filter((item) => item.state === 'not_sent' || (item.state === 'failed' && item.error?.retryable))
      .map((item) => item.id)
    setRunIds((prev) => new Set([...prev, ...ids]))
    setFatal(null)
    setRunSummary(null)
    queue.retryFailed()
  }

  // ── Remove a saved file ───────────────────────────────────────────────
  const [removing, setRemoving] = useState(false)
  const removeSaved = async ({ row, kind }) => {
    setRemoving(true)
    try {
      const res = await Service.unstageFile(batch.id, row.id, kind)
      patchRow(row.id, res?.data)
      myToaster(res?.data?.removed ? res : { status: 200, message: 'No file was saved there any more.' })
      onChanged?.()
    } catch (err) {
      const failure = unstageError(err)
      myToaster({ status: err?.status || 400, message: failure.message })
      if (failure.reload) {
        reloadTargets()
        onChanged?.()
      }
    } finally {
      if (mounted.current) setRemoving(false)
    }
  }

  // ── Confirms, preview ─────────────────────────────────────────────────
  // { kind: 'save', replacing } | { kind: 'discardAll' } | { kind: 'remove', row, fileKind }
  const [confirm, setConfirm] = useState(null)
  const [preview, setPreview] = useState(null) // { kind, row, file?, saved? }
  const onSave = () => {
    const replacing = saveable.filter((entry) => entry.replaces).sort(uploadOrder)
    if (replacing.length) setConfirm({ kind: 'save', replacing })
    else startSave()
  }
  const replacingText = (entries = []) => {
    const one = entries.length === 1
    const shown = entries
      .slice(0, 10)
      .map((entry) => `row ${entry.rowNo} (${FILE_KIND_LABEL[entry.kind] || entry.kind})`)
    const more = entries.length > shown.length ? ` and ${entries.length - shown.length} more` : ''
    return (
      `${plural(entries.length, 'file')} you added replace${one ? 's' : ''} a file already saved: ` +
      `${shown.join(', ')}${more}. The saved file${one ? ' is' : 's are'} no longer used.`
    )
  }
  const confirmSpec = {
    save: {
      title: 'Replace saved files?',
      positive: 'Save files',
      color: 'primary',
      icon: <UploadCloud02 className="text-warning/600" />,
      message: replacingText(confirm?.replacing),
    },
    discardAll: {
      title: 'Discard the files you added?',
      positive: 'Discard files',
      color: 'error',
      icon: <Trash01 className="text-error/600" />,
      message: 'Every file added here and not saved yet is taken off the rows. Saved files stay.',
    },
    remove: {
      title: 'Remove this file?',
      positive: 'Remove file',
      color: 'error',
      icon: <Trash01 className="text-error/600" />,
      message:
        confirm?.kind === 'remove'
          ? `The ${FILE_KIND_LABEL[confirm.fileKind] || 'file'} saved on row ${confirm.row.row_no} is removed.` +
            (cellState(confirm.row, confirm.fileKind) === 'staged'
              ? ' The row needs a file of this kind again before it can be processed.'
              : '')
          : '',
    },
  }[confirm?.kind]
  const onConfirm = () => {
    if (confirm?.kind === 'save') startSave()
    if (confirm?.kind === 'discardAll') discardAll()
    if (confirm?.kind === 'remove') removeSaved({ row: confirm.row, kind: confirm.fileKind })
  }

  // ── Grid: filter, search, page ────────────────────────────────────────
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useMemo(
    () =>
      debounce((e) => {
        setSearch(e.target.value.trim())
        setPage(1)
      }, 300),
    []
  )
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const rowNeedsFile = (row) =>
    gridKinds.some((kind) => cellState(row, kind, pending.get(pendingKey(row.id, kind))) === 'missing')
  const rowHasPending = (row) => gridKinds.some((kind) => pending.has(pendingKey(row.id, kind)))
  const needCount = targets.filter(rowNeedsFile).length
  const pendingRowCount = targets.filter(rowHasPending).length
  const filtered = targets.filter(
    (row) =>
      (filter === 'all' || (filter === 'needs' ? rowNeedsFile(row) : rowHasPending(row))) &&
      rowMatchesSearch(row, search)
  )
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const values = {
    data: filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    loading: read.loading && !read.data.length,
    meta: paginatorToMeta({ page: currentPage, limit: PAGE_SIZE, total: filtered.length, total_pages: totalPages }),
    checkedAll: false,
  }

  const busy = Boolean(checking) || removing
  const missingText = breakdownText(batch.missing_breakdown, FILE_KIND_LABEL)
  const needed = requiredFileLabels(batch.required_files)
  const blockedGroups = [...new Set(blocked.map((entry) => entry.sha256))].map((hash) =>
    [...new Set(blocked.filter((entry) => entry.sha256 === hash).map((entry) => entry.rowNo))].sort((a, b) => a - b)
  )
  const acceptAll = [
    batchKinds.includes('ktp_photo') && PHOTO_ACCEPT,
    batchKinds.some((kind) => kind !== 'ktp_photo') && DOCUMENT_ACCEPT,
  ]
    .filter(Boolean)
    .join(',')

  return (
    <section
      aria-labelledby="bulk-files-title"
      className="flex flex-col gap-4 rounded-xl border border-gray/200 bg-white p-5 shadow-sm"
    >
      <MyConfirmModal
        open={Boolean(confirm)}
        title={confirmSpec?.title}
        message={confirmSpec?.message}
        icon={confirmSpec?.icon}
        bgColor={confirm?.kind === 'save' ? 'bg-warning/100' : 'bg-error/100'}
        positiveButtonColor={confirmSpec?.color}
        negativeActionWord="Back"
        positiveActionWord={confirmSpec?.positive}
        onClose={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
      <FilePreview
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        kind={preview?.kind}
        file={preview?.file}
        saved={preview?.saved}
        row={preview?.row}
      />
      <AssignFileModal
        open={Boolean(assigning)}
        entry={assigning}
        targets={targets}
        pending={pending}
        keyMode={mode}
        onAssign={onAssign}
        onClose={() => setAssigning(null)}
      />
      <input ref={pickInput} type="file" className="hidden" onChange={onPicked} data-testid="bulk-cell-file-input" />

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-1">
        <h3 id="bulk-files-title" className="text-base font-semibold text-gray-900">
          Complete files
        </h3>
        <p className="text-sm text-gray-600">
          This batch needs from HR: {needed.join(', ')}. A row is ready once all of its files are saved.
          {missingText ? ` Still missing: ${missingText}.` : ''}
        </p>
      </div>

      <FileDropArea
        onFiles={onFiles}
        disabled={busy || queue.running || !loaded}
        accept={acceptAll}
        hint={<NamingHelp mode={mode} kinds={batchKinds} />}
      />

      {checking && (
        <p role="status" className="text-sm text-gray-600">
          Checking {checking.done} of {plural(checking.total, 'file')}…
        </p>
      )}

      {intake && (
        <Notice tone="info" title={`${plural(intake.total, 'file')} checked`} onDismiss={() => setIntake(null)}>
          <ul className="list-disc pl-5">
            <li>
              {plural(intake.matched, 'file')} matched to {intake.matched === 1 ? 'its row' : 'their rows'}
              {intake.replacing ? ` (${intake.replacing} replace saved files)` : ''}.
            </li>
            {intake.tray > 0 && <li>{pluralVerb(intake.tray, 'file', 'needs', 'need')} your choice (below).</li>}
            {intake.rejected > 0 && <li>{plural(intake.rejected, 'file')} cannot be used (below).</li>}
            {intake.skipped > 0 && (
              <li>{pluralVerb(intake.skipped, 'file', 'is', 'are')} already saved (same name and size): left out.</li>
            )}
            {intake.replacedPending > 0 && (
              <li>
                {pluralVerb(intake.replacedPending, 'file', 'replaces a file', 'replace files')} you had added
                before.
              </li>
            )}
            {intake.same > 0 && <li>{pluralVerb(intake.same, 'file', 'was', 'were')} added already.</li>}
            {intake.truncated && <li>Only the first {MAX_DROP_FILES} files were read.</li>}
            {intake.unreadable > 0 && <li>{plural(intake.unreadable, 'file')} could not be read.</li>}
          </ul>
        </Notice>
      )}

      {setAside > 0 && (
        <Notice tone="warning" title={`${plural(setAside, 'file')} set aside`} onDismiss={() => setSetAside(0)}>
          <p>Their rows no longer take them (the rows changed, or the settings were applied again), so they were taken off.</p>
        </Notice>
      )}

      {skipped.length > 0 && (
        <Notice tone="info" title={`${plural(skipped.length, 'file')} already saved`} onDismiss={() => setSkipped([])}>
          <p>They have the same name and size as the files saved on their rows, so they were left out.</p>
          <div>
            <MyButton type="button" color="secondary" variant="outlined" size="sm" onClick={saveSkipped}>
              Add them anyway
            </MyButton>
          </div>
        </Notice>
      )}

      {blockedGroups.length > 0 && (
        <Notice tone="error" title="The same KTP photo is on more than one row">
          <ul className="list-disc pl-5">
            {blockedGroups.slice(0, 5).map((rowNos) => (
              <li key={rowNos.join('-')}>Rows {rowNos.join(', ')}</li>
            ))}
          </ul>
          {blockedGroups.length > 5 && <p>And {blockedGroups.length - 5} more.</p>}
          <p>A photo belongs to one person: discard or reassign one of them. Until then they are not saved.</p>
        </Notice>
      )}

      <UnmatchedTray
        entries={tray}
        keyMode={mode}
        disabled={busy}
        onAssign={setAssigning}
        onPreview={(entry) => setPreview({ kind: entry.kind, file: entry.file })}
        onDismiss={(id) => setTray((prev) => prev.filter((entry) => entry.id !== id))}
        onDismissAll={() => setTray([])}
      />
      <RejectedFiles entries={rejected} onDismissAll={() => setRejected([])} />

      {/* ── Save ─────────────────────────────────────────────────────── */}
      {(pending.size > 0 || queue.running || runSummary || fatal) && (
        <div className="flex flex-col gap-3 rounded-xl border border-gray/200 bg-gray/25 px-4 py-3">
          {/* Nothing left to save: only the outcome notice of the last run stays. */}
          {(pending.size > 0 || queue.running) && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-col gap-0.5 text-sm text-gray-700">
                {queue.running ? (
                  <span role="status">
                    Saving files: {runSaved} of {runItems.length} saved
                    {runFailed ? ` · ${runFailed} not saved` : ''}
                  </span>
                ) : (
                  <span>
                    {`${plural(pending.size, 'file')} not saved yet`}
                    {blocked.length ? ` · ${blocked.length} held back (same photo on two rows)` : ''}
                  </span>
                )}
                {!queue.running && (
                  <span className="text-xs text-gray-500">Rows only change once their files are saved.</span>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-3">
                {!queue.running && (
                  <MyButton
                    type="button"
                    color="secondary"
                    variant="outlined"
                    size="md"
                    disabled={busy}
                    onClick={() => setConfirm({ kind: 'discardAll' })}
                  >
                    Discard all
                  </MyButton>
                )}
                <MyButton
                  type="button"
                  color="primary"
                  variant="filled"
                  size="md"
                  customClassname="gap-2"
                  disabled={busy || queue.running || !saveable.length}
                  onClick={onSave}
                >
                  <UploadCloud02 className="size-4" stroke="currentColor" />
                  {queue.running ? 'Saving…' : `Save ${plural(saveable.length, 'file')}`}
                </MyButton>
              </div>
            </div>
          )}
          {queue.running && runItems.length > 0 && (
            <div
              role="progressbar"
              aria-label="Files saved"
              aria-valuemin={0}
              aria-valuemax={runItems.length}
              aria-valuenow={runDone}
              className="h-1.5 w-full overflow-hidden rounded bg-gray-200"
            >
              <div
                className="h-1.5 rounded bg-brand/600 transition-all"
                style={{ width: `${(runDone / runItems.length) * 100}%` }}
              />
            </div>
          )}
          {fatal && (
            <Notice tone="error" title="Saving stopped" onDismiss={() => setFatal(null)}>
              <p>{fatal}</p>
            </Notice>
          )}
          {!queue.running && runSummary && (runSummary.saved > 0 || runSummary.failed > 0) && (
            <Notice
              tone={runSummary.failed ? 'warning' : 'success'}
              title={`${plural(runSummary.saved, 'file')} saved`}
              onDismiss={() => {
                setRunSummary(null)
                queue.clearFinished()
              }}
            >
              {runSummary.failed > 0 && <p>{plural(runSummary.failed, 'file')} could not be saved: the rows show why.</p>}
              {retryable && (
                <div>
                  <MyButton type="button" color="secondary" variant="outlined" size="sm" onClick={retryAll}>
                    Retry the files not saved
                  </MyButton>
                </div>
              )}
            </Notice>
          )}
        </div>
      )}

      {/* ── Rows ─────────────────────────────────────────────────────── */}
      <div className="flex min-h-[320px] flex-col overflow-hidden rounded-xl border border-gray/200">
        <div
          className={
            'flex flex-col gap-3 border-b border-gray/200 bg-gray/25 px-4 py-3 ' +
            'xl:flex-row xl:items-center xl:justify-between'
          }
        >
          <div className="flex flex-col gap-1">
            <MyHorizontalTabV2
              value={filter}
              fitContent
              onChange={(value) => {
                setFilter(value)
                setPage(1)
              }}
              tabs={FILTERS}
            />
            <span className="text-xs text-gray-500">
              {plural(targets.length, 'row')} waiting for files · {needCount} need a file ·{' '}
              {pendingRowCount} with files not saved yet
            </span>
          </div>
          <div className="w-full max-w-sm">
            <MyTextField
              id="input-search-bulk-files"
              placeholder="Search name, email, Employee ID or NIK"
              maxLength={SEARCH_MAX_LENGTH}
              startAdornment={<SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />}
              focusColor="#14378E"
              onChangeForm={debouncedSearch}
            />
          </div>
        </div>
        {read.error ? (
          <LoadError
            title="Could not load the rows waiting for files"
            message={read.error.status === 404 ? ROWS_HIDDEN_TEXT : read.error.message}
            onRetry={read.error.status === 404 ? undefined : reloadTargets}
          />
        ) : (
          <MyDataTable values={values} paginator onChangePagination={setPage}>
            <MyColumn
              header="Row"
              field="row_no"
              alignment="center"
              body={(row) => <span className="text-sm text-gray-500">{row.row_no}</span>}
            />
            <MyColumn header="Person" field="full_name" body={(row) => <PersonCell row={row} />} />
            {mode === 'nik' && (
              <MyColumn
                header="NIK"
                field="nik"
                body={(row) => (
                  <div className="flex flex-col items-start gap-0.5 py-1">
                    <span className="font-mono text-xs text-gray-700">{row.nik || '—'}</span>
                    {row.ktp && (
                      <button
                        type="button"
                        className="text-xs font-semibold text-brand/700 hover:underline"
                        aria-label={`e-KTP data of row ${row.row_no}`}
                        onClick={() => setPreview({ kind: null, row })}
                      >
                        e-KTP data
                      </button>
                    )}
                  </div>
                )}
              />
            )}
            {/* Before the file columns: with three of them the table scrolls
                sideways, and the status must not be the part out of view. */}
            <MyColumn
              header="Row status"
              field="status"
              body={(row) => <StatusChip {...chipOf(ROW_STATUS, row.status)} dot />}
            />
            {gridKinds.map((kind) => (
              <MyColumn
                key={kind}
                header={FILE_KIND_LABEL[kind] || kind}
                field={`file_${kind}`}
                body={(row) => {
                  const entry = pending.get(pendingKey(row.id, kind)) || null
                  return (
                    <FileCell
                      row={row}
                      kind={kind}
                      entry={entry}
                      item={entry ? queueById.get(entry.id) || null : null}
                      duplicate={entry ? duplicates.get(entry.id) || null : null}
                      locked={busy}
                      onPreview={setPreview}
                      onChoose={chooseFor}
                      onRemoveSaved={(r, k) => setConfirm({ kind: 'remove', row: r, fileKind: k })}
                      onReassign={setAssigning}
                      onDiscard={discard}
                      onRetry={retryOne}
                    />
                  )
                }}
              />
            ))}
          </MyDataTable>
        )}
      </div>
    </section>
  )
}

FileCompletionGrid.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.string.isRequired,
    needs_hr_ktp: PropTypes.bool,
    required_files: PropTypes.object,
    missing_breakdown: PropTypes.object,
  }).isRequired,
  refreshKey: PropTypes.number,
  onChanged: PropTypes.func,
  onDirtyChange: PropTypes.func,
  onPendingChange: PropTypes.func,
}

// A Process run re-renders the batch page on every event: the grid (hundreds
// of rows) only renders again when its own props change.
export default React.memo(FileCompletionGrid)
